import { z } from 'zod';
import { IsoDateTime, Uuid } from './common';

const Centavos = z.number().int().min(0).max(100_000_000);
const Bps = z.number().int().min(0).max(10_000);
export const IsoDate = z.iso.date().describe('Calendar date `YYYY-MM-DD` (São Paulo).');
export const HexColor = z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Use a #RRGGBB color');

/** Suggested channel colors (Canais board). */
export const CHANNEL_COLORS = [
  '#0F5D6B',
  '#FF5C5C',
  '#7FB2BA',
  '#9A5B00',
  '#6B5F57',
  '#27704A',
  '#E8C547',
  '#EE4D2D',
];

export const FixedFeeRange = z.object({
  fromCentavos: Centavos.describe('Range start, inclusive.'),
  toCentavos: Centavos.nullable().describe(
    'Range end, inclusive ("até R$ 78,99" = 7899); null = no limit.',
  ),
  feeCentavos: Centavos,
});
export type FixedFeeRange = z.infer<typeof FixedFeeRange>;

/** Ranges must be sorted, each with from ≤ to, without overlaps; only the last may be open-ended. */
export function fixedFeeRangesProblem(ranges: FixedFeeRange[]): string | null {
  for (let i = 0; i < ranges.length; i++) {
    const r = ranges[i]!;
    if (r.toCentavos !== null && r.toCentavos < r.fromCentavos)
      return `Faixa ${i + 1}: "até" menor que "de".`;
    const next = ranges[i + 1];
    if (!next) continue;
    if (r.toCentavos === null) return `Faixa ${i + 1} não tem limite, então precisa ser a última.`;
    if (next.fromCentavos <= r.toCentavos) return `As faixas ${i + 1} e ${i + 2} se sobrepõem.`;
  }
  return null;
}

const FixedFeeRanges = z
  .array(FixedFeeRange)
  .max(20)
  .superRefine((ranges, ctx) => {
    const problem = fixedFeeRangesProblem(ranges);
    if (problem) ctx.addIssue({ code: 'custom', message: problem });
  });

export const FeeRuleFields = z.object({
  commissionBps: Bps.describe('% of the price.'),
  paymentFeeBps: Bps.default(0).describe('Default payment fee, % of the price.'),
  fixedFees: FixedFeeRanges.default([]).describe('Fixed fee per sale by price range.'),
  shippingFromCentavos: Centavos.nullable()
    .default(null)
    .describe(
      'Seller pays shipping from this price up (free shipping); null = always (when average > 0).',
    ),
  shippingAverageCentavos: Centavos.default(0).describe(
    'Average shipping the seller pays; 0 = buyer pays.',
  ),
});
export type FeeRuleFields = z.input<typeof FeeRuleFields>;

export const FeeVersionState = z
  .enum(['draft', 'scheduled', 'current', 'past'])
  .describe(
    '`current`: in effect today; `past`: replaced; `scheduled`: takes effect later; `draft`: editable.',
  );

export const FeeVersion = z
  .object({
    id: Uuid,
    channelId: Uuid,
    number: z.number().int(),
    state: FeeVersionState,
    effectiveFrom: IsoDate,
    effectiveUntil: IsoDate.nullable().describe('Last day in effect (past versions).'),
    commissionBps: z.number().int(),
    paymentFeeBps: z.number().int(),
    fixedFees: z.array(FixedFeeRange),
    shippingFromCentavos: z.number().int().nullable(),
    shippingAverageCentavos: z.number().int(),
    note: z.string().nullable(),
    readOnly: z
      .boolean()
      .describe('Current and past versions never change: orders keep the version they used.'),
    createdAt: IsoDateTime,
  })
  .meta({ id: 'FeeVersion' });
export type FeeVersion = z.infer<typeof FeeVersion>;

export const PaymentMethod = z.object({
  name: z.string().trim().min(1).max(60),
  feeBps: Bps.nullable(),
});
export type PaymentMethod = z.infer<typeof PaymentMethod>;

export const ChannelCategory = z.object({
  id: Uuid,
  name: z.string(),
  commissionBps: z.number().int().nullable(),
});
export type ChannelCategory = z.infer<typeof ChannelCategory>;

export const Channel = z
  .object({
    id: Uuid,
    name: z.string(),
    color: z.string(),
    active: z.boolean(),
    showInQuickQuote: z.boolean(),
    defaultKitId: Uuid.nullable(),
    paymentMethods: z.array(PaymentMethod),
    payoutDays: z
      .number()
      .int()
      .nullable()
      .describe('"Prazo de recebimento": days from sale to the money landing; null = not known.'),
    categories: z.array(ChannelCategory),
    current: FeeVersion.nullable().describe('Version in effect today.'),
    versions: z.array(FeeVersion).describe('All versions, newest first.'),
    externalRef: z.string().nullable(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
  })
  .meta({ id: 'Channel' });
export type Channel = z.infer<typeof Channel>;

export const ChannelList = z.object({ data: z.array(Channel), nextCursor: z.string().nullable() });

const ChannelFields = z.object({
  name: z.string().trim().min(1).max(80),
  color: HexColor,
  active: z.boolean().default(true),
  showInQuickQuote: z.boolean().default(true),
  defaultKitId: Uuid.nullable().default(null),
  paymentMethods: z.array(PaymentMethod).max(20).default([]),
  payoutDays: z.number().int().min(0).max(120).nullable().default(null),
  externalRef: z.string().trim().max(200).nullable().default(null),
});

export const CreateChannelBody = ChannelFields.extend({
  firstVersion: FeeRuleFields.describe('v1, in effect from today.'),
}).meta({
  example: {
    name: 'ML Clássico',
    color: '#E8C547',
    firstVersion: {
      commissionBps: 1400,
      fixedFees: [{ fromCentavos: 0, toCentavos: 7899, feeCentavos: 625 }],
      shippingFromCentavos: 7900,
      shippingAverageCentavos: 2200,
    },
  },
});
export type CreateChannelBody = z.input<typeof CreateChannelBody>;

export const UpdateChannelBody = z.object({
  name: z.string().trim().min(1).max(80).optional(),
  color: HexColor.optional(),
  active: z.boolean().optional(),
  showInQuickQuote: z.boolean().optional(),
  defaultKitId: Uuid.nullable().optional(),
  paymentMethods: z.array(PaymentMethod).max(20).optional(),
  payoutDays: z.number().int().min(0).max(120).nullable().optional(),
  externalRef: z.string().trim().max(200).nullable().optional(),
});
export type UpdateChannelBody = z.input<typeof UpdateChannelBody>;

export const PutCategoriesBody = z.object({
  categories: z
    .array(z.object({ name: z.string().trim().min(1).max(120), commissionBps: Bps.nullable() }))
    .max(100),
});
export type PutCategoriesBody = z.input<typeof PutCategoriesBody>;

export const CreateDraftBody = z.object({
  effectiveFrom: IsoDate.optional().describe('Defaults to tomorrow.'),
});

export const UpdateFeeVersionBody = z.object({
  effectiveFrom: IsoDate.optional(),
  commissionBps: Bps.optional(),
  paymentFeeBps: Bps.optional(),
  fixedFees: FixedFeeRanges.optional(),
  shippingFromCentavos: Centavos.nullable().optional(),
  shippingAverageCentavos: Centavos.optional(),
  note: z.string().trim().max(500).nullable().optional(),
});
export type UpdateFeeVersionBody = z.input<typeof UpdateFeeVersionBody>;

export const FeeTestBody = z
  .object({
    channelId: Uuid,
    versionId: Uuid.optional().describe('Defaults to the version in effect today.'),
    priceCentavos: Centavos,
    categoryId: Uuid.optional().describe('Use this category commission when it has one.'),
    paymentMethod: z
      .string()
      .optional()
      .describe('Name of a channel payment method (e.g. "Crédito à vista").'),
  })
  .meta({ example: { channelId: '00000000-0000-4000-8000-000000000001', priceCentavos: 5490 } });
export type FeeTestBody = z.input<typeof FeeTestBody>;

export const FeeTestResult = z
  .object({
    versionId: Uuid,
    versionNumber: z.number().int(),
    priceCentavos: z.number().int(),
    commission: z.object({
      bps: z.number().int(),
      centavos: z.number().int(),
      fromCategory: z.boolean(),
    }),
    fixedFee: z.object({ centavos: z.number().int(), range: FixedFeeRange.nullable() }),
    shipping: z.object({ centavos: z.number().int(), sellerPays: z.boolean() }),
    payment: z.object({
      bps: z.number().int(),
      centavos: z.number().int(),
      method: z.string().nullable(),
    }),
    totalFeesCentavos: z.number().int(),
    youReceiveCentavos: z.number().int(),
    feesShareBps: z.number().int().describe('Fees as a share of the price.'),
  })
  .meta({ id: 'FeeTestResult' });
export type FeeTestResult = z.infer<typeof FeeTestResult>;
